import type { DashboardSaveV2Request, VehicleInsurance } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrencyForDisplay } from "../../../../../components/ui/uikit/currencyInput";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { CurrencyField, DateField, FullCoverageField, LossPayeeField, PhoneField, TextField } from "./Fields";
import {
  AGENT_NAME_MAX_LENGTH,
  buildInsuranceDefaultValues,
  DEDUCTIBLE_MAX_LENGTH,
  type FullCoverageValue,
  insuranceInformationSchema,
  INSURER_NAME_MAX_LENGTH,
  POLICY_NUMBER_MAX_LENGTH,
  toInsuranceSaveRequest,
} from "./InsuranceInformation.schema";

interface InsuranceInformationProperties {
  readonly insurance?: VehicleInsurance;
  /** From `financial.amountFinanced` on the dashboard response — not part of `VehicleInsurance` itself. */
  readonly amountFinanced?: number;
  /** Whether every field below Amount Financed is typeable/selectable — condition TBD; defaults to disabled (today's behavior). */
  readonly editable?: boolean;
}

/**
 * RHF + Zod, same pattern as ContractVehicleInformation.tsx — see
 * InsuranceInformation.schema.ts for the validation rules. Unlike that spike,
 * every field here except Full Coverage already has a real backing value on
 * `VehicleInsurance` today, so `defaultValues` are built from the real
 * `insurance` prop rather than a fully-static mock.
 *
 * Registered with the save orchestrator as "insurance", contributing `vehicleInsurance`. Contract Vehicle
 * contributes the same leg (Lic Plate only), so `useSaveAllSections` merges the two objects key by key.
 * Insurance is not locked by pre-approval (spec 5.1); `editable` is just the tab-level read-only verdict.
 */
export function InsuranceInformation({ insurance, amountFinanced, editable = false }: InsuranceInformationProperties) {
  const { t } = useTranslation();
  const disabled = !editable;
  const form = useForm({
    resolver: zodResolver(insuranceInformationSchema),
    mode: "onBlur",
    defaultValues: buildInsuranceDefaultValues(insurance),
  });
  const { control } = form;
  const fullCoverage = useController({ control, name: "fullCoverage" });
  const policyNumber = useController({ control, name: "policyNumber" });
  const insurerName = useController({ control, name: "insurerName" });
  const insurerPhone = useController({ control, name: "insurerPhone" });
  const agentName = useController({ control, name: "agentName" });
  const expirationDate = useController({ control, name: "expirationDate" });
  const comprehensiveDeductibleAmount = useController({ control, name: "comprehensiveDeductibleAmount" });
  const collisionDeductibleAmount = useController({ control, name: "collisionDeductibleAmount" });
  const isCaLossPayee = useController({ control, name: "isCaLossPayee" });

  const [saveError, setSaveError] = useState<string>();

  useRegisterSaveableSection("insurance", {
    // Report-only validation (spec 9.2): shows the red fields but never gates the save.
    validate: async () => disabled || form.trigger(),
    getPayload: (): Partial<DashboardSaveV2Request> =>
      disabled ? {} : { vehicleInsurance: toInsuranceSaveRequest(form.getValues()) },
    onSaved: () => {
      setSaveError(undefined);
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  const deductiblesDisabled = disabled || fullCoverage.field.value !== "Y";

  // Selecting anything other than "Yes" disables and immediately nulls out both deductibles.
  // `null`, not `undefined` — see the schema's comment on these two fields for why.
  function handleFullCoverageChange(value: FullCoverageValue) {
    fullCoverage.field.onChange(value);
    if (value === "Y") {
      void form.trigger(["comprehensiveDeductibleAmount", "collisionDeductibleAmount"]);
    } else {
      comprehensiveDeductibleAmount.field.onChange(null);
      collisionDeductibleAmount.field.onChange(null);
      void form.trigger(["comprehensiveDeductibleAmount", "collisionDeductibleAmount"]);
    }
  }

  // A new company name invalidates the phone that belonged to the previous one.
  function handleInsurerNameChange(value: string) {
    insurerName.field.onChange(value);
    insurerPhone.field.onChange("");
  }

  return (
    <div>
      <SubsectionHeader>{t("vehicle.insurance.heading")}</SubsectionHeader>
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <LabeledValue
              label={t("vehicle.insurance.amountFinanced")}
              value={formatCurrencyForDisplay(amountFinanced)}
            />
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              {fullCoverage.field.value !== undefined && (
                <FullCoverageField
                  label={t("vehicle.insurance.fullCoverage")}
                  value={fullCoverage.field.value}
                  onChange={handleFullCoverageChange}
                  disabled={disabled}
                />
              )}
              <TextField
                label={t("vehicle.insurance.policyNumber")}
                value={policyNumber.field.value}
                onChange={policyNumber.field.onChange}
                onBlur={policyNumber.field.onBlur}
                error={policyNumber.fieldState.error}
                disabled={disabled}
                maxLength={POLICY_NUMBER_MAX_LENGTH}
              />
            </div>
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              <TextField
                label={t("vehicle.insurance.companyName")}
                value={insurerName.field.value}
                onChange={handleInsurerNameChange}
                onBlur={insurerName.field.onBlur}
                error={insurerName.fieldState.error}
                disabled={disabled}
                maxLength={INSURER_NAME_MAX_LENGTH}
                uppercase
              />
              <PhoneField
                label={t("vehicle.insurance.companyPhone")}
                value={insurerPhone.field.value}
                onChange={insurerPhone.field.onChange}
                onBlur={insurerPhone.field.onBlur}
                error={insurerPhone.fieldState.error}
                disabled={disabled}
              />
              <TextField
                label={t("vehicle.insurance.agent")}
                value={agentName.field.value}
                onChange={agentName.field.onChange}
                onBlur={agentName.field.onBlur}
                error={agentName.fieldState.error}
                disabled={disabled}
                maxLength={AGENT_NAME_MAX_LENGTH}
                uppercase
              />
              <DateField
                label={t("vehicle.insurance.expirationDate")}
                value={expirationDate.field.value}
                onChange={expirationDate.field.onChange}
                onBlur={expirationDate.field.onBlur}
                error={expirationDate.fieldState.error}
                disabled={disabled}
              />
              <CurrencyField
                label={t("vehicle.insurance.comprehensiveDeductible")}
                value={comprehensiveDeductibleAmount.field.value}
                onChange={comprehensiveDeductibleAmount.field.onChange}
                onBlur={comprehensiveDeductibleAmount.field.onBlur}
                error={comprehensiveDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
                maxLength={DEDUCTIBLE_MAX_LENGTH}
              />
              <CurrencyField
                label={t("vehicle.insurance.collisionDeductible")}
                value={collisionDeductibleAmount.field.value}
                onChange={collisionDeductibleAmount.field.onChange}
                onBlur={collisionDeductibleAmount.field.onBlur}
                error={collisionDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
                maxLength={DEDUCTIBLE_MAX_LENGTH}
              />
              <LossPayeeField
                label={t("vehicle.insurance.caLossPayee")}
                value={isCaLossPayee.field.value}
                onChange={isCaLossPayee.field.onChange}
                disabled={disabled}
              />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
